import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { useProductUrls, useSiteMetadata, useSmartlingStrings } from '@lucid/hooks';

import PreFooterCta, { type PreFooterCtaProps } from './pre-footer-cta';
import LayoutContext from '../../context/layout-context';

export interface NewsletterCTAProps extends PreFooterCtaProps {
  /** The url for the newsletter sign-up page */
  newsletterSignUpUrl: string;
  /** Allows component to receive additional styles */
  className?: string;
}

const NewsletterCTA: FC<PropsWithChildren<NewsletterCTAProps>> = ({ newsletterSignUpUrl, className, ...rest }) => {
  const { shortCode, urlLangPrefix } = useContext(LayoutContext);
  const { product } = useSiteMetadata();
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_newsletter_cta_2',
      'mktg_nav_sign_up_free',
      `mktg_blog_cta_lucid${product !== 'spark' ? 'chart' : 'spark'}_intro`,
      'mktg_newsletter_subscribe',
    ],
    shortCode,
  });
  const { pricingUrl } = useProductUrls(urlLangPrefix);

  return shortCode === 'en' ? (
    <PreFooterCta
      {...rest}
      hasSSO
      className={className}
      ctas={[
        {
          id: '',
          default_link_text: withSmartling('mktg_newsletter_subscribe') || 'Subscribe to our newsletter',
          final_url: newsletterSignUpUrl || '/',
        },
      ]}
      data-testid="newsletter-cta"
      heading={withSmartling('mktg_newsletter_cta_2') || 'Bring your bright ideas to life.'}
    />
  ) : (
    <PreFooterCta
      {...rest}
      hasSSO
      className={className}
      ctas={[
        {
          id: '',
          default_link_text: withSmartling('mktg_nav_sign_up_free') || 'Sign up free',
          final_url: pricingUrl,
        },
      ]}
      heading={
        withSmartling(`mktg_blog_cta_lucid${product !== 'spark' ? 'chart' : 'spark'}_intro`) ||
        'Start diagramming with Lucidchart today—try it for free!'
      }
    />
  );
};

export default NewsletterCTA;
